// Plan-CSATUIModal.md §B.1 (2026-05-15) — post-conversation customer
// satisfaction modal. Replaces the `record_csat` tool that Gemini Live
// kept skipping; CSAT is now a deterministic UI step AFTER
// conversation_ended teardown completes.
//
// Rendered at the Widget.tsx parent level so it overlays both chat and
// voice surfaces.
//
// Plan-VoiceWidgetRevisions Item 1 (2026-05-25) — the voice tab USED to
// also speak the question + ending spiel via the browser Web Speech API.
// That spoken prompt is now removed; CSAT is visual-only on both tabs.
//
// Three internal states:
//   form        → 5 stars + optional comment + Submit / Skip
//   submitting  → POST in flight, spinner
//   thanks      → ending spiel, auto-closes after 5s
//
// Backend contract (Plan §A.3):
//   POST /api/widget/conversations/{conversation_id}/csat
//     body: { rating?: 1-5, comment?: ≤2000, declined?: boolean }
//     Submit → rating + comment, no declined
//     Skip   → declined: true, no rating/comment
//     Idempotent: 200 {status: "already_recorded"} on re-submit
//
// Accessibility:
//   - role="dialog" + aria-modal="true"
//   - Esc key + backdrop click → Skip path (idempotent on backend so
//     a quick Esc-then-Submit double-fire is safe)
//   - Focus trap on stars (autofocus first star on mount)
//   - aria-live="polite" on the thanks heading so screen readers
//     announce the spiel
//
// Responsive: 375px mobile → full-width card with 16px gutters; star
// buttons 36px tap targets per WCAG. Dark mode uses standard dark
// palette matching the maintenance card.

import React, { useEffect, useRef, useState } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import { widgetAPI } from '../../services/api';

interface CSATModalProps {
  conversationId: string;
  brandColor: string;
  theme: 'light' | 'dark';
  /** Plan-VoiceWidgetRevisions Item 1 (2026-05-25) — formerly toggled
   *  Web Speech API TTS in voice mode. TTS is now removed; this prop is
   *  retained for call-site compatibility but is a no-op. */
  useTTS: boolean;
  onClose: () => void;
}

type ModalState = 'form' | 'submitting' | 'thanks';

// Plan-VoiceWidgetRevisions Item 1 (2026-05-25) — the TTS_QUESTION /
// TTS_THANKS strings and the speakIfSupported / cancelTTS helpers were
// removed. The CSAT modal no longer speaks its prompt aloud on any tab;
// see the no-op effect below.

const CSATModal: React.FC<CSATModalProps> = ({
  conversationId,
  brandColor,
  theme,
  useTTS,
  onClose,
}) => {
  const [state, setState] = useState<ModalState>('form');
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const firstStarRef = useRef<HTMLButtonElement | null>(null);
  // Track which path was attempted so Retry can replay it without the
  // user having to re-pick stars / re-click Skip.
  const lastSubmitPathRef = useRef<'submit' | 'skip' | null>(null);

  const isDark = theme === 'dark';

  // Focus the first star on mount so keyboard users land in the
  // primary interaction.
  useEffect(() => {
    if (state === 'form') {
      firstStarRef.current?.focus();
    }
  }, [state]);

  // Plan-VoiceWidgetRevisions Item 1 (2026-05-25) — the spoken CSAT
  // prompt is removed. The browser Web Speech API read the question +
  // ending spiel aloud in voice mode, which read awkwardly over the
  // visual star form. CSAT is now visual-only on BOTH tabs. The
  // `useTTS` prop is retained for call-site compatibility but is now a
  // no-op; the speak/cancel helpers and the TTS_* strings were removed
  // so no call site can re-introduce speech. We keep a defensive
  // speechSynthesis.cancel() on unmount in case any OTHER surface left
  // an utterance queued when this modal mounts.
  useEffect(() => {
    void useTTS; // intentionally ignored — TTS disabled (Item 1)
    return () => {
      if (typeof window === 'undefined') return;
      if (!('speechSynthesis' in window)) return;
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [state, useTTS]);

  // Auto-dismiss thanks state after 5s. User can hit the X earlier.
  useEffect(() => {
    if (state !== 'thanks') return;
    const t = window.setTimeout(() => {
      onClose();
    }, 5000);
    return () => window.clearTimeout(t);
  }, [state, onClose]);

  // Esc → Skip path (idempotent on backend, so worst case a quick
  // Esc-after-Submit fires a no-op `declined` request).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (state === 'form') {
        void doSkip();
      } else if (state === 'thanks') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const doSubmit = async () => {
    if (rating < 1 || rating > 5) return;
    lastSubmitPathRef.current = 'submit';
    setError(null);
    setState('submitting');
    try {
      await widgetAPI.submitCSAT(conversationId, {
        rating,
        comment: comment.trim() ? comment.trim() : undefined,
      });
      setState('thanks');
    } catch (err: any) {
      // Defensive: treat 200 {status: "already_recorded"} as success
      // — `widgetAPI.submitCSAT` resolves on 2xx so this branch only
      // fires on 4xx/5xx/network. We surface a retry CTA.
      console.warn('[CSATModal] submit failed', err);
      setError('Could not submit. Please try again.');
      setState('form');
    }
  };

  const doSkip = async () => {
    lastSubmitPathRef.current = 'skip';
    setError(null);
    setState('submitting');
    try {
      await widgetAPI.submitCSAT(conversationId, { declined: true });
      setState('thanks');
    } catch (err: any) {
      console.warn('[CSATModal] skip failed', err);
      // Even on a network error the user "skipped" — log it but
      // proceed to thanks so we don't trap them in the modal.
      setState('thanks');
    }
  };

  const doRetry = async () => {
    setError(null);
    if (lastSubmitPathRef.current === 'skip') {
      await doSkip();
    } else {
      await doSubmit();
    }
  };

  const cardBg = isDark
    ? 'bg-gray-900 border-gray-800 text-gray-100'
    : 'bg-white border-gray-200 text-gray-800';
  const subtleText = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputClasses = isDark
    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-6"
      onClick={() => {
        // Backdrop click — only allow during form state, never
        // during submitting or thanks (would lose state).
        if (state === 'form') void doSkip();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="csat-heading"
    >
      <div
        className={`relative w-full max-w-sm rounded-2xl shadow-2xl border p-5 sm:p-6 ${cardBg}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X — visible in all states; allows escape from thanks
            without waiting for the 5s auto-dismiss. */}
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 rounded-full hover:opacity-80 ${
            isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100'
          }`}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {state === 'form' && (
          <div className="space-y-4">
            <h3
              id="csat-heading"
              className="text-lg font-semibold pr-8"
            >
              How was your experience?
            </h3>

            {/* Star row — 5 buttons, 36px tap targets, hover preview. */}
            <div className="flex items-center justify-center gap-1.5 sm:gap-2">
              {[1, 2, 3, 4, 5].map((n) => {
                const showFilled = n <= (hoverRating || rating);
                return (
                  <button
                    key={n}
                    ref={n === 1 ? firstStarRef : undefined}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1"
                    style={
                      showFilled
                        ? { color: brandColor }
                        : { color: isDark ? '#4b5563' : '#d1d5db' }
                    }
                    aria-label={`Rate ${n} out of 5 stars`}
                  >
                    <Star
                      className="w-7 h-7 sm:w-8 sm:h-8"
                      strokeWidth={1.5}
                      fill={showFilled ? brandColor : 'none'}
                    />
                  </button>
                );
              })}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Any additional feedback? (optional)"
              maxLength={2000}
              rows={3}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 resize-none ${inputClasses}`}
              style={{ '--tw-ring-color': brandColor } as any}
            />

            {error && (
              <div className="flex items-center justify-between gap-2 text-xs text-red-500 dark:text-red-400">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={doRetry}
                  className="font-semibold underline hover:opacity-80"
                >
                  Retry
                </button>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={doSkip}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isDark
                    ? 'text-gray-400 hover:bg-gray-800'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={doSubmit}
                disabled={rating < 1}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-shadow"
                style={{ backgroundColor: brandColor }}
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {state === 'submitting' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2
              className="w-6 h-6 animate-spin"
              style={{ color: brandColor }}
              aria-hidden="true"
            />
            <p className={`text-sm ${subtleText}`}>Submitting…</p>
          </div>
        )}

        {state === 'thanks' && (
          <div
            className="text-center py-4 sm:py-6 space-y-2"
            aria-live="polite"
          >
            <h3
              id="csat-heading"
              className="text-lg font-semibold"
            >
              Thank you for chatting with us!
            </h3>
            <p className={`text-sm ${subtleText}`}>Have a great day.</p>
            <button
              type="button"
              onClick={onClose}
              className={`mt-4 text-xs font-medium underline ${subtleText} hover:opacity-80`}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATModal;
